'use client';

import React from 'react';
import { X, Timer, MousePointerClick, Target, Trophy, AlertTriangle } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export default function RulesModal({ isOpen, onClose }: Props) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-2xl victory-entrance">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <AlertTriangle size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">How to Play</h2>
              <p className="text-[11px] text-muted-foreground">AdversaryMatch Rules</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            aria-label="Close rules modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="space-y-3">
            {[
              {
                icon: <Target size={15} className="text-primary" />,
                title: 'Objective',
                text: 'Match all 8 pairs of CrowdStrike named adversaries. Each adversary appears twice in the 4×4 grid.',
              },
              {
                icon: <MousePointerClick size={15} className="text-accent" />,
                title: 'Gameplay',
                text: 'Click any face-down card to reveal it. Click a second card — if they match, they stay face-up. If not, both flip back after a short delay.',
              },
              {
                icon: <Timer size={15} className="text-warning" />,
                title: 'Timer',
                text: 'The timer starts on your first flip and stops when you find all 8 pairs. Complete the board as fast as possible.',
              },
              {
                icon: <Trophy size={15} className="text-success" />,
                title: 'Scoring Tiers',
                text: '',
                custom: (
                  <div className="mt-1 grid grid-cols-2 gap-1.5">
                    {[
                      { tier: 'ELITE', desc: '<20s', cls: 'tier-elite' },
                      { tier: 'EXPERT', desc: '20–30s', cls: 'tier-expert' },
                      { tier: 'ANALYST', desc: '30–40s', cls: 'tier-analyst' },
                      { tier: 'RECRUIT', desc: 'Any other result', cls: 'tier-recruit' },
                    ].map((t) => (
                      <div key={`tier-${t.tier}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold ${t.cls}`}>
                        <span className="font-mono font-bold">{t.tier}</span>
                        <span className="text-[10px] opacity-80 font-normal">{t.desc}</span>
                      </div>
                    ))}
                  </div>
                ),
              },
            ].map((rule, i) => (
              <div key={`rule-${i}`} className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                  {rule.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{rule.title}</p>
                  {rule.text && <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">{rule.text}</p>}
                  {rule.custom}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-[11px] text-primary/80 leading-relaxed">
              <span className="font-bold">Pro tip:</span> Sign in to save your scores to the global leaderboard and track your personal best over time.
            </p>
          </div>
        </div>

        <div className="p-5 pt-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent transition-all duration-150 active:scale-95"
          >
            Start Playing
          </button>
        </div>
      </div>
    </div>
  );
}